import * as React from 'react';
import { Divider, Flex, Input, Select, Text } from '@ttoss/ui';
import {
  Form,
  FormField,
  FormFieldCheckbox,
  FormFieldInput,
  useForm,
  yup,
  yupResolver,
} from '@ttoss/forms';
import { HeaderRegister } from '../../components/HeaderRegister/HeaderRegister';
import { SelectSpecies } from '../Species/SelectSpecies';
import { useClones } from '../Species/useClones';
import { useNavigate, useParams } from 'react-router-dom';
import { usePropertyProductions } from './usePropertyProductions';
import { useSiflorApi } from 'src/providers/SiflorApiProvider/useSiflorApi';
import Button from '../../components/Button/Button';

export const FormFieldClone = ({
  name,
  label,
}: {
  name: string;
  label: string;
}) => {
  const { clones } = useClones();

  return (
    <FormField
      name={name}
      label={label}
      render={({ field: { value, onChange } }) => {
        const selectedClone = clones.find((clone) => {
          return clone.id === value;
        });

        return (
          <Flex
            sx={{
              flexDirection: 'column',
            }}
          >
            <Select value={value} onChange={onChange}>
              <option value="">Selecione ou digite abaixo</option>
              {clones.map((clone) => {
                return (
                  <option key={clone.id} value={clone.id}>
                    {clone.name}
                  </option>
                );
              })}
            </Select>
            {/* Only show the input if selectedClone doesn't match. */}
            <Input value={selectedClone ? '' : value} onChange={onChange} />
          </Flex>
        );
      }}
    />
  );
};

const schema = yup
  .object({
    speciesId: yup.string().required('O campo espécie é obrigatório'),
    cloneId: yup.string().required('O campo clone é obrigatório'),
    planted: yup.boolean().required(),
    readyToHarvest: yup.boolean().required(),
    harvested: yup.boolean().required(),
  })
  .required();

type FormValues = yup.InferType<typeof schema> & /**
 * Remove when schema is typed.
 */ { [key: string]: any };

export const RegistrationProduction = () => {
  const { propertyId, propertyProductionId } = useParams<{
    propertyId: string;
    propertyProductionId?: string;
  }>();

  const navigate = useNavigate();

  const { siflorApi } = useSiflorApi();

  const formMethods = useForm<FormValues>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      speciesId: '',
      cloneId: '',
      planted: true,
      readyToHarvest: false,
      harvested: false,
      plantedAreaSize: 'ha',
      plantingYear: 2009,
      nextCutDate: '09/23',
      averageDiameter: 85,
      averageSize: 390,
    },
  });

  const { productions } = usePropertyProductions({ propertyId });

  const { reset } = formMethods;

  React.useEffect(() => {
    if (propertyProductionId) {
      const production = productions.find((production) => {
        return production.id === propertyProductionId;
      });

      if (production) {
        reset({
          speciesId: production.clone.species.id,
          cloneId: production.clone.id,
          planted: production.planted,
          readyToHarvest: production.readyToHarvest,
          harvested: production.harvested,
        });
      }
    }
  }, [reset, propertyProductionId, productions]);

  const navigateToProperty = () => {
    navigate(`/propriedade/${propertyId}`);
  };

  const [error, setError] = React.useState<Error | null>(null);

  const handleSubmit = async (data: FormValues) => {
    try {
      setError(null);

      const production = {
        planted: data.planted,
        readyToHarvest: data.readyToHarvest,
        harvested: data.harvested,
        propertyId,
        cloneId: data.cloneId,
      };

      if (propertyProductionId) {
        await siflorApi.updatePropertyProduction(
          propertyProductionId,
          production
        );
      } else {
        await siflorApi.createPropertyProduction(propertyId, production);
      }

      navigateToProperty();
    } catch (error) {
      setError(error);
    }
  };

  return (
    <Flex sx={{ flexDirection: 'column', width: '100%' }}>
      <HeaderRegister
        title={
          <Flex sx={{ flexDirection: 'column', gap: 6, alignItems: 'center' }}>
            <Text
              sx={{
                color: 'background',
                fontFamily: 'body',
                fontSize: 3,
                fontWeight: 'body',
              }}
            >
              Cadastrando Produção para
            </Text>
            <Text
              sx={{
                color: 'background',
                fontFamily: 'heading',
                fontSize: 3,
              }}
            >
              Fazenda Santa Amélia
            </Text>
          </Flex>
        }
      />
      <Flex
        sx={{
          alignItems: 'center',
          flexDirection: 'column',
          paddingTop: '36px',
          width: '100%',
          maxWidth: '800px',
          alignSelf: 'center',
          paddingX: 6,
        }}
      >
        <Form
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            paddingBottom: 8,
          }}
          {...formMethods}
          onSubmit={handleSubmit}
        >
          <Text
            sx={{
              color: 'dark',
              fontSize: 2,
              fontFamily: 'heading',
              marginBottom: 6,
              textAlign: 'center',
            }}
          >
            Dados da Produção
          </Text>

          <FormField
            label="Selecione a espécie*"
            name="speciesId"
            render={({ field: { value, onChange } }) => {
              return <SelectSpecies values={[value]} onSelect={onChange} />;
            }}
          />

          <FormFieldClone label="Selecione o clone*" name="cloneId" />

          <Text
            sx={{
              color: 'dark',
              fontSize: 2,
              fontFamily: 'heading',
              alignSelf: 'center',
              marginBottom: 6,
              marginTop: 8,
            }}
          >
            Clone
          </Text>

          <Divider sx={{ marginTop: 8 }} />

          <Text
            sx={{
              paddingY: 7,
              paddingLeft: 5,
              fontSize: 2,
              fontFamily: 'heading',
              color: 'dark',
            }}
          >
            Quais estados de madeira são ofertados?
          </Text>

          <Flex
            sx={{
              flexDirection: 'column',
              gap: 5,
              marginBottom: 8,
              paddingX: 6,
              'div>div>label': {
                fontSize: 3,
              },
            }}
          >
            <FormFieldCheckbox name="planted" label="Plantada" />
            <FormFieldCheckbox
              name="readyToHarvest"
              label="Pronta para colher"
            />
            <FormFieldCheckbox name="harvested" label="Colhida" />
          </Flex>

          <Divider />

          <Flex
            sx={{
              flexDirection: 'column',
              maxWidth: [143, 270],
              marginTop: 9,
              marginBottom: 10,
              marginLeft: 5,
              gap: 6,
            }}
          >
            <FormFieldInput
              name="plantedAreaSize"
              label="Tamanho da área plantada"
            />

            <FormFieldInput name="plantingYear" label="Ano do plantio" />

            <FormFieldInput name="nextCutDate" label="Data do próximo corte:" />

            <FormFieldInput
              name="averageDiameter"
              label="Diâmetro médio da tora"
            />

            <FormFieldInput name="averageSize" label="Tamanho médio da tora" />
          </Flex>

          {error && <Text sx={{ color: 'red' }}>{error.message}</Text>}

          <Flex
            sx={{
              flexDirection: 'column',
              gap: 6,
              maxWidth: 279,
              alignSelf: 'center',
              width: '100%',
            }}
          >
            <Button
              color="primary"
              type="button"
              outlined
              onClick={navigateToProperty}
            >
              Cancelar
            </Button>

            <Button type="submit">Pronto!</Button>
          </Flex>
        </Form>
      </Flex>
    </Flex>
  );
};
